<template>
  <v-container fluid>
    <section v-if="$vuetify.breakpoint.mdAndUp" id="pipeline-desktop-section">
      <div v-if="loading === false">
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-2" hover tile flat>
              <v-card-text class="black--text">
                <div class="text-body-1 font-weight-bold text-uppercase my-3">
                  Total
                </div>
                <div class="text-h6 font-weight-light" style="line-height: 1">
                  {{ originalItems.length | currency("", 0) }}
                </div>
                <div class="text-h5 font-weight-light success--text">
                  {{
                    originalItems
                      .map((x) => x.baseLoan)
                      .reduce(function (a, b) {
                        return a + b;
                      }, 0) | currency("$", 0)
                  }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col
            v-for="summary in loanSummaryList.filter((x) => x.count > 0)"
            :key="`desktop-summary-${summary.codeId}`"
            cols="12"
            sm="6"
            md="3"
          >
            <v-card
              class="text-center pa-2"
              hover
              tile
              flat
              @click="addStatusFilter(summary.displayName)"
            >
              <v-card-text class="black--text">
                <div class="text-body-1 font-weight-bold text-uppercase my-3">
                  {{ summary.displayName }}
                </div>
                <div class="text-h6 font-weight-light" style="line-height: 1">
                  {{ summary.count | currency("", 0) }}
                </div>
                <div class="text-h5 font-weight-light success--text">
                  {{ summary.amt | currency("$", 0) }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-card class="my-6">
          <v-card-title class="black--text text-body-1 font-weight-bold">
            Pipeline List
            <div>
              <div>
                {{ filterOptions.registeredStartDate }}
              </div>
              {{ filterOptions.registeredEndDate }}
            </div>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <div class="my-4 d-flex">
              <!-- <v-btn class="mr-2" color="background" elevation="0" disabled>Print</v-btn> -->
              <downloadexcel
                :header="excelTotal"
                :fetch="getExcelReport"
                type="xls"
                name="Pipeline"
              >
                <v-btn color="success" elevation="0"> Excel </v-btn>
              </downloadexcel>
              <v-spacer></v-spacer>
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    aria-label="Refresh"
                    icon
                    @click="pageRefresh"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Refresh pipeline</span>
              </v-tooltip>
            </div>
            <v-data-table
              id="pipeline-table"
              :headers="dataTableHeaders"
              :items="filteredItems"
              item-key="fileName"
              :items-per-page="10"
              show-expand
              fixed-header
              dense
              sort-by="registeredDate"
              :sort-desc="true"
              class="table--striped table--bordered"
              calculate-widths
              :footer-props="{
                'items-per-page-options': [10, 25, 50],
              }"
            >
              <template v-slot:[`body.prepend`]="{}">
                <tr id="filter-body">
                  <td class="pa-1">
                    <v-text-field
                      v-model="filterOptions.loanNumber"
                      @input="filterPipeline"
                      color="grey"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </td>
                  <td class="pa-1">
                    <v-select
                      v-model="filterOptions.loanStatusName"
                      flat
                      :items="filterOptions.loanStatusNameItems"
                      @change="filterPipeline"
                      chips
                      small-chips
                      deletable-chips
                      multiple
                      clearable
                      dense
                      outlined
                      hide-details
                      :menu-props="{ bottom: true, offsetY: true }"
                      color="grey"
                    >
                      <template v-slot:[`item`]="data">
                        <v-list-item-content>
                          <v-list-item-title>
                            <div v-html="data.item"></div>
                          </v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </td>
                  <td class="pa-1">
                    <v-text-field
                      v-model="filterOptions.borrowerName"
                      flat
                      @input="filterPipeline"
                      dense
                      outlined
                      hide-details
                      color="grey"
                    ></v-text-field>
                  </td>
                  <td class="pa-1">
                    <v-text-field
                      v-model="filterOptions.loanProgramCode"
                      flat
                      @input="filterPipeline"
                      dense
                      outlined
                      hide-details
                      color="grey"
                    ></v-text-field>
                  </td>
                  <td class="pa-1">
                    <v-select
                      v-model="filterOptions.purposeOfLoan"
                      flat
                      :items="filterOptions.purposeOfLoanItems"
                      @change="filterPipeline"
                      auto-select-first
                      chips
                      small-chips
                      deletable-chips
                      multiple
                      clearable
                      dense
                      outlined
                      hide-details
                      :menu-props="{ bottom: true, offsetY: true }"
                      color="grey"
                    >
                      <template v-slot:[`item`]="data">
                        <v-list-item-content>
                          <v-list-item-title>
                            <div v-html="data.item"></div>
                          </v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </td>
                  <td class="pa-1">
                    <v-text-field
                      v-model="filterOptions.broker"
                      flat
                      @input="filterPipeline"
                      dense
                      outlined
                      hide-details
                      color="grey"
                    ></v-text-field>
                  </td>
                  <td
                    class="pa-1"
                    v-if="
                      $store.getters[`isAdmin`] ||
                      $store.getters[`isOperationManager`]
                    "
                  >
                    <v-select
                      v-model="filterOptions.subPropState"
                      flat
                      :items="filterOptions.subPropStateItems"
                      @change="filterPipeline"
                      chips
                      small-chips
                      deletable-chips
                      multiple
                      clearable
                      dense
                      outlined
                      hide-details
                      :menu-props="{ bottom: true, offsetY: true }"
                      color="grey"
                    >
                      <template v-slot:[`item`]="data">
                        <v-list-item-content>
                          <v-list-item-title>
                            <div v-html="data.item"></div>
                          </v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </td>
                  <td
                    class="pa-1"
                    v-if="
                      $store.getters[`isAdmin`] ||
                      $store.getters[`isOperationManager`]
                    "
                  >
                    <v-select
                      v-model="filterOptions.accountExecutive"
                      flat
                      :items="filterOptions.accountExecutiveItems"
                      @change="filterPipeline"
                      chips
                      small-chips
                      deletable-chips
                      multiple
                      clearable
                      dense
                      outlined
                      hide-details
                      :menu-props="{ bottom: true, offsetY: true }"
                      color="grey"
                    >
                      <template v-slot:[`item`]="data">
                        <v-list-item-content>
                          <v-list-item-title>
                            <div v-html="data.item"></div>
                          </v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </td>
                  <td class="pa-1">
                    <v-text-field
                      flat
                      placeholder="Min"
                      v-model="filterOptions.baseLoanMin"
                      v-mask="$store.state.mask.currency"
                      prefix="$"
                      @change="filterPipeline"
                      dense
                      outlined
                      hide-details
                      color="grey"
                    ></v-text-field>
                    <v-text-field
                      flat
                      placeholder="Max"
                      v-model="filterOptions.baseLoanMax"
                      v-mask="$store.state.mask.currency"
                      prefix="$"
                      @change="filterPipeline"
                      dense
                      outlined
                      hide-details
                      color="grey"
                    ></v-text-field>
                  </td>
                  <td class="pa-1">
                    <v-menu
                      v-model="filterOptions.lockExpirationStartMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="filterOptions.lockExpirationStartDate"
                          flat
                          solo
                          placeholder="Start"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="
                            filterOptions.lockExpirationStartDate = null
                          "
                          @input="filterPipeline"
                          readonly
                          clearable
                          dense
                          outlined
                          hide-details
                          color="grey"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="filterOptions.lockExpirationStartDate"
                        @input="filterOptions.lockExpirationStartMenu = false"
                        @change="filterPipeline"
                      ></v-date-picker>
                    </v-menu>
                    <v-menu
                      v-model="filterOptions.lockExpirationEndMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="filterOptions.lockExpirationEndDate"
                          flat
                          solo
                          placeholder="End"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="
                            filterOptions.lockExpirationEndDate = null
                          "
                          @input="filterPipeline"
                          readonly
                          clearable
                          dense
                          outlined
                          hide-details
                          color="grey"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="filterOptions.lockExpirationEndDate"
                        @input="filterOptions.lockExpirationEndMenu = false"
                        @change="filterPipeline"
                      ></v-date-picker>
                    </v-menu>
                  </td>
                  <td class="pa-1">
                    <v-menu
                      v-model="filterOptions.registeredStartMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="filterOptions.registeredStartDate"
                          flat
                          solo
                          placeholder="Start"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="
                            filterOptions.registeredStartDate = null
                          "
                          @input="filterPipeline"
                          readonly
                          clearable
                          dense
                          outlined
                          hide-details
                          color="grey"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="filterOptions.registeredStartDate"
                        @input="filterOptions.registeredStartMenu = false"
                        @change="filterPipeline"
                      ></v-date-picker>
                    </v-menu>
                    <v-menu
                      v-model="filterOptions.registeredEndMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="filterOptions.registeredEndDate"
                          flat
                          solo
                          placeholder="End"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="filterOptions.registeredEndDate = null"
                          @input="filterPipeline"
                          readonly
                          clearable
                          dense
                          outlined
                          hide-details
                          color="grey"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="filterOptions.registeredEndDate"
                        @input="filterOptions.registeredEndMenu = false"
                        @change="filterPipeline"
                      ></v-date-picker>
                    </v-menu>
                  </td>
                  <td class="pa-1">
                    <v-menu
                      v-model="filterOptions.approvalStartMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="filterOptions.approvalStartDate"
                          flat
                          solo
                          placeholder="Start"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="filterOptions.approvalStartDate = null"
                          @input="filterPipeline"
                          readonly
                          clearable
                          dense
                          outlined
                          hide-details
                          color="grey"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="filterOptions.approvalStartDate"
                        @input="filterOptions.approvalStartMenu = false"
                        @change="filterPipeline"
                      ></v-date-picker>
                    </v-menu>
                    <v-menu
                      v-model="filterOptions.approvalEndMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="filterOptions.approvalEndDate"
                          flat
                          solo
                          placeholder="End"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="filterOptions.approvalEndDate = null"
                          @input="filterPipeline"
                          readonly
                          clearable
                          dense
                          outlined
                          hide-details
                          color="grey"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="filterOptions.approvalEndDate"
                        @input="filterOptions.approvalEndMenu = false"
                        @change="filterPipeline"
                      ></v-date-picker>
                    </v-menu>
                  </td>
                  <td class="pa-1">
                    <v-menu
                      v-model="filterOptions.fundedStartMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="filterOptions.fundedStartDate"
                          flat
                          solo
                          placeholder="Start"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="filterOptions.fundedStartDate = null"
                          @input="filterPipeline"
                          readonly
                          clearable
                          dense
                          outlined
                          hide-details
                          color="grey"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="filterOptions.fundedStartDate"
                        @input="filterOptions.fundedStartMenu = false"
                        @change="filterPipeline"
                      ></v-date-picker>
                    </v-menu>
                    <v-menu
                      v-model="filterOptions.fundedEndMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="filterOptions.fundedEndDate"
                          flat
                          solo
                          placeholder="End"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="filterOptions.fundedEndDate = null"
                          @input="filterPipeline"
                          readonly
                          clearable
                          dense
                          outlined
                          hide-details
                          color="grey"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="filterOptions.fundedEndDate"
                        @input="filterOptions.fundedEndMenu = false"
                        @change="filterPipeline"
                      ></v-date-picker>
                    </v-menu>
                  </td>
                  <td></td>
                </tr>
              </template>
              <template v-slot:[`item.fileName`]="{ item }">
                <router-link
                  class="text-decoration-none secondary--text font-weight-medium"
                  :to="{
                    path: '/pipeline/loan-detail',
                    query: { loanNumber: item.fileName },
                  }"
                >
                  {{ item.fileName }}
                </router-link>
              </template>
              <template v-slot:[`item.loanStatusName`]="{ item }">
                {{ item.loanStatusName }}
              </template>
              <template v-slot:[`item.purposeOfLoan`]="{ item }">
                {{ item.purposeOfLoan }}
              </template>
              <template v-slot:[`item.baseLoan`]="{ item }">
                <div>{{ item.baseLoan | currency }}</div>
              </template>
              <template v-slot:[`item.lockExpiration`]="{ item }">
                <div v-if="item.lockExpiration">
                  {{ item.lockExpiration | moment("MM/DD/YYYY") }}
                </div>
              </template>
              <template v-slot:[`item.registeredDate`]="{ item }">
                <div v-if="item.registeredDate">
                  {{ item.registeredDate | moment("MM/DD/YYYY") }}
                </div>
              </template>
              <template v-slot:[`item.approvalDate`]="{ item }">
                <div v-if="item.approvalDate">
                  {{ item.approvalDate | moment("MM/DD/YYYY") }}
                </div>
              </template>
              <template v-slot:[`item.fundedDate`]="{ item }">
                <div v-if="item.fundedDate">
                  {{ item.fundedDate | moment("MM/DD/YYYY") }}
                </div>
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length">
                  <v-row>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">Property Address: </span>
                      <span>
                        {{ item.subPropFullAddress }}
                        <ExternalLinkModal :to="item.googleMap">
                          <v-btn class="text-decoration-none" text icon x-small>
                            <v-icon small>mdi-google-maps</v-icon>
                          </v-btn>
                        </ExternalLinkModal>
                        <ExternalLinkModal :to="item.zillowMap">
                          <v-btn class="text-decoration-none" text icon x-small>
                            <v-icon>mdi-alpha-z-circle-outline</v-icon>
                          </v-btn>
                        </ExternalLinkModal>
                      </span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">Property Type: </span>
                      <span>
                        {{ item.propertyTypeName }}
                      </span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">Occupancy Type: </span>
                      <span>{{ item.occupancyTypeName }}</span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">Rate: </span>
                      <span>{{ item.intRate | percent(3, 1) }}</span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">Sales Price: </span>
                      <span>{{ item.purPrice | currency() }}</span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">Appraised Value: </span>
                      <span>{{ item.appraisedValue | currency() }}</span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">LTV / CLTV: </span>
                      <span
                        >{{ item.ltv | percent(2, 1) }} /
                        {{ item.cltv | percent(2, 1) }}</span
                      >
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">DTI: </span>
                      <span>{{ item.dti | percent(2, 1) }}</span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">Doc Type: </span>
                      <span>{{
                        toDisplayName(
                          $store.state.byteProEnumList.documentationType,
                          item.documentationType
                        )
                      }}</span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">FICO Score: </span>
                      <span>{{ item.ficoScore }}</span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">Underwriter: </span>
                      <span>{{ item.underwriter }}</span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold"
                        >Scheduled Funding Date:
                      </span>
                      <span v-if="item.schedFundingDate">{{
                        item.schedFundingDate | moment("MM/DD/YYYY")
                      }}</span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">Account Executive: </span>
                      <span>{{ item.accountExecutive }}</span>
                    </v-col>
                    <v-col cols="12" md="4">
                      <span class="font-weight-bold">Funder: </span>
                      <span>{{ item.funder }}</span>
                    </v-col>
                    <v-col v-if="item.incomeDocType" cols="12" md="4">
                      <span class="font-weight-bold">Income Doc Type: </span>
                      <span>{{ item.incomeDocType }}</span>
                    </v-col>
                  </v-row>
                </td>
              </template>
              <template v-slot:[`footer`]="{}">
                <v-divider></v-divider>
                <div class="d-flex align-center my-2">
                  <div class="mx-2">
                    Total Loans:
                    <span class="font-weight-bold">
                      {{ filteredItems.length | currency("", 0) }}
                    </span>
                  </div>
                  <div class="mx-2">
                    Total Loan Amount:
                    <span class="font-weight-bold">
                      {{
                        (filteredItems && filteredItems.length
                          ? filteredItems
                              .map((x) => x.baseLoan)
                              .reduce((a, b) => a + b)
                          : 0) | currency("$", 0)
                      }}
                    </span>
                  </div>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </div>
      <div v-else>
        <v-row>
          <v-col
            v-for="index in 12"
            :key="`desktop-summary-skeleton-${index}`"
            cols="12"
            sm="6"
            md="3"
          >
            <v-skeleton-loader
              width="100%"
              height="150"
              type="card"
            ></v-skeleton-loader>
          </v-col>
        </v-row>
        <v-skeleton-loader type="table"></v-skeleton-loader>
      </div>
    </section>
    <section v-else id="pipeline-mobile-section">
      <div class="d-flex my-4" v-if="!loading">
        <downloadexcel
          :header="excelTotal"
          :fetch="getExcelReport"
          type="xls"
          name="Pipeline"
        >
          <v-btn color="success" elevation="0"> Excel </v-btn>
        </downloadexcel>
        <v-spacer></v-spacer>
        <v-btn color="info" elevation="0" @click="loanSummaryDialog = true">
          Loan Summary
        </v-btn>
        <v-spacer></v-spacer>
        <v-badge
          :value="originalItems !== filteredItems"
          color="error"
          bordered
          overlap
          dot
        >
          <v-btn
            aria-label="Refresh"
            @click="filterOptions.dialog = true"
            elevation="0"
          >
            <v-icon>mdi-filter</v-icon>
          </v-btn>
        </v-badge>
      </div>
      <div class="d-flex my-4" v-else>
        <v-skeleton-loader type="button"></v-skeleton-loader>
        <v-spacer></v-spacer>
        <v-skeleton-loader type="button"></v-skeleton-loader>
        <v-spacer></v-spacer>
        <v-skeleton-loader type="button"></v-skeleton-loader>
      </div>
      <v-dialog v-model="loanSummaryDialog" max-width="600">
        <v-card color="background">
          <v-toolbar color="info" dark flat>
            <v-card color="info" class="text-body-1" flat>
              Loan Summary
            </v-card>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="loanSummaryDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <v-card class="text-center mt-2" hover tile flat>
              <v-card-text class="black--text">
                <div class="text-body-1 font-weight-bold text-uppercase my-3">
                  Total
                </div>
                <div class="text-h6 font-weight-light" style="line-height: 1">
                  {{ originalItems.length | currency("", 0) }}
                </div>
                <div class="text-h5 font-weight-light success--text">
                  {{
                    originalItems
                      .map((x) => x.baseLoan)
                      .reduce(function (a, b) {
                        return a + b;
                      }, 0) | currency("$", 0)
                  }}
                </div>
              </v-card-text>
            </v-card>
            <v-card
              v-for="summary in loanSummaryList.filter((x) => x.count > 0)"
              :key="`Summary-${summary.status}`"
              class="text-center mt-2"
              hover
              tile
              flat
              @click="addStatusFilter(summary.displayName)"
            >
              <v-card-text class="black--text">
                <div class="text-body-1 font-weight-bold text-uppercase my-3">
                  {{ summary.displayName }}
                </div>
                <div class="text-h6 font-weight-light" style="line-height: 1">
                  {{ summary.count | currency("", 0) }}
                </div>
                <div class="text-h5 font-weight-light success--text">
                  {{ summary.amt | currency("$", 0) }}
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="filterOptions.dialog"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
      >
        <v-card>
          <v-toolbar flat>
            <!-- <v-card color="primary" class="text-body-1" flat>
              Use Pipeline filter option?
            </v-card> -->
            <!-- <v-btn
              text
              color="error"
              @click="initFilterOptions()"
            >
              RESET
            </v-btn> -->
            <v-btn
              color="primary"
              elevation="0"
              @click="filterOptions.dialog = false"
            >
              SAVE
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn icon @click="filterOptions.dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <br />
            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  label="Loan Status"
                  placeholder="Loan Status"
                  v-model="filterOptions.loanStatusName"
                  flat
                  :items="filterOptions.loanStatusNameItems"
                  @change="filterPipeline"
                  chips
                  small-chips
                  deletable-chips
                  multiple
                  clearable
                  dense
                  outlined
                  hide-details
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:[`item`]="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        <div v-html="data.item"></div>
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  flat
                  label="Borrower"
                  placeholder="Borrower"
                  v-model="filterOptions.borrowerName"
                  @change="filterPipeline"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  flat
                  label="Product Code"
                  placeholder="Product Code"
                  v-model="filterOptions.loanProgramCode"
                  @change="filterPipeline"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  label="Loan Purpose"
                  placeholder="Loan Purpose"
                  v-model="filterOptions.purposeOfLoan"
                  flat
                  :items="filterOptions.purposeOfLoanItems"
                  @change="filterPipeline"
                  chips
                  small-chips
                  deletable-chips
                  multiple
                  clearable
                  dense
                  outlined
                  hide-details
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:[`item`]="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        <div v-html="data.item"></div>
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  flat
                  label="Broker"
                  placeholder="Broker"
                  v-model="filterOptions.broker"
                  @change="filterPipeline"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>

              <v-col
                v-if="
                  $store.getters[`isAdmin`] ||
                  $store.getters[`isOperationManager`]
                "
                cols="12"
                sm="6"
              >
                <v-select
                  label="State"
                  placeholder="State"
                  v-model="filterOptions.subPropState"
                  flat
                  :items="filterOptions.subPropStateItems"
                  @change="filterPipeline"
                  chips
                  small-chips
                  deletable-chips
                  multiple
                  clearable
                  dense
                  outlined
                  hide-details
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:[`item`]="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        <div v-html="data.item"></div>
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>

              <v-col
                v-if="
                  $store.getters[`isAdmin`] ||
                  $store.getters[`isOperationManager`]
                "
                cols="12"
                sm="6"
              >
                <v-select
                  label="Account Executive"
                  placeholder="Account Executive"
                  v-model="filterOptions.accountExecutive"
                  flat
                  :items="filterOptions.accountExecutiveItems"
                  @change="filterPipeline"
                  chips
                  small-chips
                  deletable-chips
                  multiple
                  clearable
                  dense
                  outlined
                  hide-details
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:[`item`]="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        <div v-html="data.item"></div>
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  flat
                  label="Loan Amount(Min)"
                  placeholder="Min"
                  v-model="filterOptions.baseLoanMin"
                  v-mask="$store.state.mask.currency"
                  prefix="$"
                  @change="filterPipeline"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  flat
                  label="Loan Amount(Max)"
                  placeholder="Max"
                  v-model="filterOptions.baseLoanMax"
                  v-mask="$store.state.mask.currency"
                  prefix="$"
                  @change="filterPipeline"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="filterOptions.lockExpirationStartMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filterOptions.lockExpirationStartDate"
                      flat
                      label="Lock Expiration Start Date"
                      placeholder="YYYY-MM-DD"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="
                        filterOptions.lockExpirationStartDate = null
                      "
                      @input="filterPipeline"
                      readonly
                      clearable
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filterOptions.lockExpirationStartDate"
                    @input="filterOptions.lockExpirationStartMenu = false"
                    @change="filterPipeline"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="filterOptions.lockExpirationEndMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filterOptions.lockExpirationEndDate"
                      flat
                      label="Lock Expiration End Date"
                      placeholder="YYYY-MM-DD"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="filterOptions.lockExpirationEndDate = null"
                      @input="filterPipeline"
                      readonly
                      clearable
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filterOptions.lockExpirationEndDate"
                    @input="filterOptions.lockExpirationEndMenu = false"
                    @change="filterPipeline"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="filterOptions.registeredStartMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filterOptions.registeredStartDate"
                      flat
                      label="Register Start Date"
                      placeholder="YYYY-MM-DD"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="filterOptions.registeredStartDate = null"
                      @input="filterPipeline"
                      readonly
                      clearable
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filterOptions.registeredStartDate"
                    @input="filterOptions.registeredStartMenu = false"
                    @change="filterPipeline"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="filterOptions.registeredEndMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filterOptions.registeredEndDate"
                      flat
                      label="Register End Date"
                      placeholder="YYYY-MM-DD"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="filterOptions.registeredEndDate = null"
                      @input="filterPipeline"
                      readonly
                      clearable
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filterOptions.registeredEndDate"
                    @input="filterOptions.registeredEndMenu = false"
                    @change="filterPipeline"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="filterOptions.approvalStartMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filterOptions.approvalStartDate"
                      flat
                      label="Approval Start Date"
                      placeholder="YYYY-MM-DD"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="filterOptions.approvalStartDate = null"
                      @input="filterPipeline"
                      readonly
                      clearable
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filterOptions.approvalStartDate"
                    @input="filterOptions.approvalStartMenu = false"
                    @change="filterPipeline"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="filterOptions.approvalEndMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filterOptions.approvalEndDate"
                      flat
                      label="Approval End Date"
                      placeholder="YYYY-MM-DD"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="filterOptions.approvalEndDate = null"
                      @input="filterPipeline"
                      readonly
                      clearable
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filterOptions.approvalEndDate"
                    @input="filterOptions.approvalEndMenu = false"
                    @change="filterPipeline"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="filterOptions.fundedStartMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filterOptions.fundedStartDate"
                      flat
                      label="Funded Start Date"
                      placeholder="YYYY-MM-DD"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="filterOptions.fundedStartDate = null"
                      @input="filterPipeline"
                      readonly
                      clearable
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filterOptions.fundedStartDate"
                    @input="filterOptions.fundedStartMenu = false"
                    @change="filterPipeline"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="filterOptions.fundedEndMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filterOptions.fundedEndDate"
                      flat
                      label="Funded End Date"
                      placeholder="YYYY-MM-DD"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="filterOptions.fundedEndDate = null"
                      @input="filterPipeline"
                      readonly
                      clearable
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filterOptions.fundedEndDate"
                    @input="filterOptions.fundedEndMenu = false"
                    @change="filterPipeline"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-data-iterator
        :items="filteredItems"
        :items-per-page.sync="itemsPerPage"
        :page.sync="page"
        hide-default-footer
        :loading="loading"
      >
        <template v-slot:header>
          <v-toolbar dark color="primary" class="mb-1">
            <v-text-field
              label="Search with Loan #"
              v-model="filterOptions.loanNumber"
              @input="filterPipeline"
              clearable
              flat
              solo-inverted
              hide-details
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-btn aria-label="Refresh" icon @click="pageRefresh">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card class="d-flex pa-1" tile flat>
            <div>
              Total Loans:
              <span class="font-weight-bold">{{
                filteredItems.length | currency("", 0)
              }}</span>
            </div>
            <div class="ml-auto">
              Total Loan Amount:
              <span class="font-weight-bold">
                {{
                  (filteredItems && filteredItems.length
                    ? filteredItems
                        .map((x) => x.baseLoan)
                        .reduce((a, b) => a + b)
                    : 0) | currency("$", 0)
                }}
              </span>
            </div>
          </v-card>
        </template>

        <template v-slot:loading>
          <v-skeleton-loader
            v-for="index in 8"
            :key="`mobile-skeleton-${index}`"
            class="my-2"
            :type="'card-heading'"
          ></v-skeleton-loader>
        </template>

        <template v-slot:no-data>
          <v-card class="text-center" tile flat>
            <span class="error--text">No Data</span>
          </v-card>
        </template>

        <template v-slot:default="props">
          <v-row class="my-2" dense>
            <v-col
              v-for="item in props.items"
              :key="item.fileName"
              cols="12"
              md="6"
            >
              <v-card>
                <v-card-text class="d-flex align-center py-1">
                  <router-link
                    class="text-decoration-none font-weight-bold secondary--text"
                    :to="{
                      path: '/pipeline/loan-detail',
                      query: { loanNumber: item['fileName'] },
                    }"
                  >
                    {{ item["fileName"] }} - {{ item["borrowerLastAndFirst"] }}
                  </router-link>
                  <v-spacer></v-spacer>
                  <v-btn icon @click="item.show = !item.show">
                    <v-icon>{{
                      item.show ? "mdi-chevron-up" : "mdi-chevron-down"
                    }}</v-icon>
                  </v-btn>
                </v-card-text>

                <v-divider></v-divider>
                <v-list dense v-show="item.show">
                  <v-list-item>
                    <v-list-item-content> Status: </v-list-item-content>
                    <v-list-item-content class="align-end">
                      {{ item["loanStatusName"] }}
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content> Product Code: </v-list-item-content>
                    <v-list-item-content class="align-end">
                      {{ item["loanProgramCode"] }}
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content> Purpose: </v-list-item-content>
                    <v-list-item-content class="align-end">
                      {{ item["loanPurposeName"] }}
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content> Broker: </v-list-item-content>
                    <v-list-item-content class="align-end">
                      {{ item["broker"] }}
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item
                    v-if="
                      $store.getters[`isAdmin`] ||
                      $store.getters[`isOperationManager`]
                    "
                  >
                    <v-list-item-content> State: </v-list-item-content>
                    <v-list-item-content class="align-end">
                      {{ item["subPropState"] }}
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item
                    v-if="
                      $store.getters[`isAdmin`] ||
                      $store.getters[`isOperationManager`]
                    "
                  >
                    <v-list-item-content> AE: </v-list-item-content>
                    <v-list-item-content class="align-end">
                      {{ item["accountExecutive"] }}
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content> Loan Amount: </v-list-item-content>
                    <v-list-item-content class="align-end">
                      {{ item["baseLoan"] | currency() }}
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content>
                      Lock Expiration:
                    </v-list-item-content>
                    <v-list-item-content class="align-end">
                      <span v-if="item['lockExpiration']">{{
                        item["lockExpiration"] | moment("MM/DD/YYYY")
                      }}</span>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content>
                      Registered Date:
                    </v-list-item-content>
                    <v-list-item-content class="align-end">
                      <span v-if="item['registeredDate']">{{
                        item["registeredDate"] | moment("MM/DD/YYYY")
                      }}</span>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content> Approval Date: </v-list-item-content>
                    <v-list-item-content class="align-end">
                      <span v-if="item['approvalDate']">{{
                        item["approvalDate"] | moment("MM/DD/YYYY")
                      }}</span>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content> Funded Date: </v-list-item-content>
                    <v-list-item-content class="align-end">
                      <span v-if="item['fundedDate']">{{
                        item["fundedDate"] | moment("MM/DD/YYYY")
                      }}</span>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <template v-slot:footer>
          <v-row class="px-4" align="center" justify="center">
            <span>Items per page</span>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  dark
                  text
                  color="primary"
                  class="ml-2"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ itemsPerPage }}
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(number, index) in itemsPerPageArray"
                  :key="index"
                  @click="updateItemsPerPage(number)"
                >
                  <v-list-item-title>{{ number }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-spacer></v-spacer>

            <span class="mr-2"> Page {{ page }} of {{ numberOfPages }} </span>
            <v-btn
              fab
              dark
              color="primary"
              class="mr-1"
              @click="formerPage"
              x-small
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              fab
              dark
              color="primary"
              class="ml-1"
              @click="nextPage"
              x-small
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-row>
        </template>
      </v-data-iterator>
    </section>
  </v-container>
</template>
<style>
.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  color: #fff;
  font-size: 12px;
}

.theme--light.v-data-table
  .v-data-table-header
  th.sortable.active
  .v-data-table-header__icon {
  color: #fff;
}

.theme--light.v-data-table .v-data-table-header th.sortable:hover,
.theme--light.v-data-table .v-data-table-header th.sortable.active {
  color: #fff;
}

.table--striped.theme--light.v-data-table thead th {
  background-color: #122a4b;
}
.table--striped.theme--dark.v-data-table thead th {
  background-color: #122a4b;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  padding: 0 6px;
}
</style>

<script>
import { convert } from "@/mixins";
import downloadexcel from "vue-json-excel";
import ExternalLinkModal from "@/components/ExternalLinkModal.vue";

export default {
  name: "Pipeline",
  components: {
    downloadexcel,
    ExternalLinkModal,
  },
  created() {
    this.initFilterOptions();
  },
  async mounted() {
    this.loading = true;

    this.loanSummaryList = await this.$store
      .dispatch("mstcode/getAll", "LoanStatus")
      .then((resp) => resp.data.filter((x) => x.reference8 == "Y"))
      .catch(() => []);

    const response = await this.$store
      .dispatch("fileDataApi/getPipeline")
      .then((resp) => resp)
      .catch((err) => err);

    this.originalItems = response.data;
    this.filteredItems = this.originalItems;

    this.filterOptions.loanStatusNameItems = [
      ...new Set(this.originalItems.map((x) => x.loanStatusName)),
    ].filter((x) => !!x);

    this.filterOptions.purposeOfLoanItems = [
      ...new Set(this.originalItems.map((x) => x.purposeOfLoan)),
    ].filter((x) => !!x);

    this.filterOptions.subPropStateItems = [
      ...new Set(this.originalItems.map((x) => x.subPropState)),
    ].filter((x) => !!x);

    this.filterOptions.accountExecutiveItems = [
      ...new Set(this.originalItems.map((x) => x.accountExecutive)),
    ].filter((x) => !!x);

    this.loanSummaryList.forEach((element) => {
      const filteredList = this.originalItems.filter(
        (x) => x.loanStatus == element.codeValue
      );
      if (filteredList) {
        element.amt = filteredList
          .map((x) => x.baseLoan)
          .reduce(function (a, b) {
            return a + b;
          }, 0);
        element.count = filteredList.length;
      }
    });

    this.loading = false;
  },
  data() {
    return {
      loading: false,
      itemsPerPageArray: [10, 25, 50],
      filter: {},
      page: 1,
      itemsPerPage: 10,
      originalItems: [],
      filteredItems: [],
      filterOptions: null,
      loanSummaryDialog: false,
      loanSummaryList: [],
      excelTotal: 0,
      excelPipeline: [],
      dataTableHeaders:
        this.$store.getters[`isAdmin`] ||
        this.$store.getters[`isOperationManager`]
          ? [
              { text: "Loan #", value: "fileName", width: "70px" },
              { text: "Status", value: "loanStatusName", width: "90px" },
              {
                text: "Borrower",
                value: "borrowerLastAndFirst",
                width: "110px",
              },
              {
                text: "Product Code",
                value: "loanProgramCode",
                width: "130px",
              },
              { text: "Purpose", value: "purposeOfLoan", width: "100px" },
              { text: "Broker", value: "broker", width: "150px" },
              { text: "State", value: "subPropState", width: "40px" },
              { text: "AE", value: "accountExecutive", width: "80px" },
              { text: "Loan Amount", value: "baseLoan", width: "110px" },
              {
                text: "Lock Expiration",
                value: "lockExpiration",
                width: "120px",
              },
              {
                text: "Registered Date",
                value: "registeredDate",
                width: "120px",
              },
              { text: "Approval Date", value: "approvalDate", width: "110px" },
              { text: "Funded Date", value: "fundedDate", width: "110px" },
              {
                text: "∇",
                value: "data-table-expand",
                align: "center",
                width: "10px",
              },
            ]
          : [
              { text: "Loan #", value: "fileName", width: "70px" },
              { text: "Status", value: "loanStatusName", width: "90px" },
              {
                text: "Borrower",
                value: "borrowerLastAndFirst",
                width: "110px",
              },
              {
                text: "Product Code",
                value: "loanProgramCode",
                width: "130px",
              },
              { text: "Purpose", value: "purposeOfLoan", width: "90px" },
              { text: "Broker", value: "broker", width: "150px" },
              { text: "Loan Amount", value: "baseLoan", width: "110px" },
              {
                text: "Lock Expiration",
                value: "lockExpiration",
                width: "120px",
              },
              {
                text: "Registered Date",
                value: "registeredDate",
                width: "120px",
              },
              { text: "Approval Date", value: "approvalDate", width: "110px" },
              { text: "Funded Date", value: "fundedDate", width: "110px" },
              {
                text: "∇",
                value: "data-table-expand",
                align: "center",
                width: "10px",
              },
            ],
    };
  },
  computed: {
    numberOfPages() {
      return Math.ceil(this.filteredItems.length / this.itemsPerPage);
    },
    filteredKeys() {
      return this.keys.filter((key) => key !== "fileName");
    },
  },
  methods: {
    nextPage() {
      if (this.page + 1 <= this.numberOfPages) this.page += 1;
    },
    formerPage() {
      if (this.page - 1 >= 1) this.page -= 1;
    },
    updateItemsPerPage(number) {
      this.itemsPerPage = number;
    },
    filterPipeline() {
      this.filteredItems = this.originalItems;
      const startTime = "00:00:00";
      const endTime = "23:59:59";

      if (this.filterOptions.loanNumber) {
        this.filteredItems = this.filteredItems.filter((x) =>
          x.fileName
            .toLowerCase()
            .includes(this.filterOptions.loanNumber.toLowerCase())
        );
      }

      if (
        this.filterOptions.loanStatusName &&
        this.filterOptions.loanStatusName.length > 0
      ) {
        var filteredLoanStatusName = JSON.parse(
          JSON.stringify(this.filterOptions.loanStatusName)
        );
        this.filteredItems = this.filteredItems.filter(function (item) {
          return filteredLoanStatusName.includes(item.loanStatusName);
        });
      }

      if (this.filterOptions.borrowerName) {
        this.filteredItems = this.filteredItems.filter((x) =>
          (x.borrowerLastAndFirst ?? "")
            .toLowerCase()
            .includes(this.filterOptions.borrowerName.toLowerCase())
        );
      }

      if (this.filterOptions.loanProgramCode) {
        this.filteredItems = this.filteredItems.filter((x) =>
          (x.loanProgramCode ?? "")
            .toLowerCase()
            .includes(this.filterOptions.loanProgramCode.toLowerCase())
        );
      }

      if (
        this.filterOptions.purposeOfLoan &&
        this.filterOptions.purposeOfLoan.length > 0
      ) {
        var filteredLoanpurpose = JSON.parse(
          JSON.stringify(this.filterOptions.purposeOfLoan)
        );
        this.filteredItems = this.filteredItems.filter(function (item) {
          return filteredLoanpurpose.includes(item.purposeOfLoan);
        });
      }

      if (this.filterOptions.broker) {
        this.filteredItems = this.filteredItems.filter((x) =>
          (x.broker ?? "")
            .toLowerCase()
            .includes(this.filterOptions.broker.toLowerCase())
        );
      }

      if (
        this.filterOptions.subPropState &&
        this.filterOptions.subPropState.length > 0
      ) {
        var filteredSubPropState = JSON.parse(
          JSON.stringify(this.filterOptions.subPropState)
        );
        this.filteredItems = this.filteredItems.filter(function (item) {
          return filteredSubPropState.includes(item.subPropState);
        });
      }

      if (
        this.filterOptions.accountExecutive &&
        this.filterOptions.accountExecutive.length > 0
      ) {
        var filteredaccountExecutive = JSON.parse(
          JSON.stringify(this.filterOptions.accountExecutive)
        );
        this.filteredItems = this.filteredItems.filter(function (item) {
          return filteredaccountExecutive.includes(item.accountExecutive);
        });
      }

      if (this.filterOptions.baseLoanMin) {
        this.filteredItems = this.filteredItems.filter(
          (x) =>
            x.baseLoan >=
            Number(this.filterOptions.baseLoanMin.replace(/[^0-9.-]+/g, ""))
        );
      }

      if (this.filterOptions.baseLoanMax) {
        this.filteredItems = this.filteredItems.filter(
          (x) =>
            x.baseLoan <=
            Number(this.filterOptions.baseLoanMax.replace(/[^0-9.-]+/g, ""))
        );
      }

      if (this.filterOptions.lockExpirationStartDate) {
        this.filteredItems = this.filteredItems.filter(
          (x) =>
            this.$moment(x.lockExpiration) >=
            this.$moment(
              `${this.filterOptions.lockExpirationStartDate} ${startTime}`
            )
        );
      }

      if (this.filterOptions.lockExpirationEndDate) {
        this.filteredItems = this.filteredItems.filter(
          (x) =>
            this.$moment(x.lockExpiration) <=
            this.$moment(
              `${this.filterOptions.lockExpirationEndDate} ${endTime}`
            )
        );
      }

      if (this.filterOptions.registeredStartDate) {
        this.filteredItems = this.filteredItems.filter(
          (x) =>
            this.$moment(x.registeredDate) >=
            this.$moment(
              `${this.filterOptions.registeredStartDate} ${startTime}`
            )
        );
      }

      if (this.filterOptions.registeredEndDate) {
        this.filteredItems = this.filteredItems.filter(
          (x) =>
            this.$moment(x.registeredDate) <=
            this.$moment(`${this.filterOptions.registeredEndDate} ${endTime}`)
        );
      }

      if (this.filterOptions.approvalStartDate) {
        this.filteredItems = this.filteredItems.filter(
          (x) =>
            this.$moment(x.approvalDate) >=
            this.$moment(`${this.filterOptions.approvalStartDate} ${startTime}`)
        );
      }

      if (this.filterOptions.approvalEndDate) {
        this.filteredItems = this.filteredItems.filter(
          (x) =>
            this.$moment(x.approvalDate) <=
            this.$moment(`${this.filterOptions.approvalEndDate} ${endTime}`)
        );
      }

      if (this.filterOptions.fundedStartDate) {
        this.filteredItems = this.filteredItems.filter(
          (x) =>
            this.$moment(x.fundedDate) >=
            this.$moment(`${this.filterOptions.fundedStartDate} ${startTime}`)
        );
      }

      if (this.filterOptions.fundedEndDate) {
        this.filteredItems = this.filteredItems.filter(
          (x) =>
            this.$moment(x.fundedDate) <=
            this.$moment(`${this.filterOptions.fundedEndDate} ${endTime}`)
        );
      }
    },
    initFilterOptions() {
      this.filterOptions = {
        dialog: false,
        loanNumber: null,
        loanStatusName: [],
        loanStatusNameItems: [],
        borrowerName: null,
        loanProgramCode: null,
        loanProgramCodeItems: [],
        loanPurpose: null,
        loanPurposeItems: [],
        purposeOfLoan: null,
        purposeOfLoanItems: [],
        broker: null,
        subPropState: null,
        subPropStateItems: [],
        accountExecutive: null,
        accountExecutiveItems: [],
        baseLoanMin: null,
        baseLoanMax: null,
        lockExpirationStartMenu: false,
        lockExpirationStartDate: null,
        lockExpirationEndMenu: false,
        lockExpirationEndDate: null,
        registeredStartMenu: false,
        registeredStartDate: null,
        registeredEndMenu: false,
        registeredEndDate: null,
        approvalStartMenu: false,
        approvalStartDate: null,
        approvalEndMenu: false,
        approvalEndDate: null,
        fundedStartMenu: false,
        fundedStartDate: null,
        fundedEndMenu: false,
        fundedEndDate: null,
      };
    },
    getExcelReport() {
      this.excelPipeline = [];
      this.filteredItems.forEach((e) => {
        this.excelPipeline.push({
          fileName: e.fileName,
          status: e.loanStatusName,
          borrower: e.borrowerFullName,
          program: e.loanProgramCode,
          loanAmount: this.$options.filters.currency(e.baseLoan),
          purpose: e.loanPurposeName,
          lockStartDate: e.lockStartDate
            ? this.$moment(e.lockStartDate).format("MM/DD/YYYY")
            : null,
          lockExpiration: e.lockExpiration
            ? this.$moment(e.lockExpiration).format("MM/DD/YYYY")
            : null,
          registeredDate: e.registeredDate
            ? this.$moment(e.registeredDate).format("MM/DD/YYYY")
            : null,
          approvalDate: e.approvalDate
            ? this.$moment(e.approvalDate).format("MM/DD/YYYY")
            : null,
          fundedDate: e.fundedDate
            ? this.$moment(e.fundedDate).format("MM/DD/YYYY")
            : null,
          broker: e.broker,
          propertyStreet: e.subPropStreet,
          propertyCity: e.subPropCity,
          propertyState: e.subPropState,
          propertyZip:
            !!e.subPropZip && e.subPropZip.length >= 5
              ? e.subPropZip.substring(0, 5)
              : "",
          propertyType: e.propertyTypeName,
          occupancy: e.occupancyTypeName,
          rate: this.$options.filters.percent(e.intRate, 3, 1),
          salesPrice: this.$options.filters.currency(e.purPrice),
          appraisedValue: this.$options.filters.currency(e.appraisedValue),
          ltv: this.$options.filters.percent(e.ltv, 2, 1),
          cltv: this.$options.filters.percent(e.cltv, 2, 1),
          dti: this.$options.filters.percent(e.dti, 2, 1),
          docType: e.documentationTypeName,
          ficoScore: e.ficoScore,
          underwriter: e.underwriter,
          accountExecutive: e.accountExecutive,
          funder: e.funder,
        });
      });

      this.excelTotal = `Total Loans: ${this.$options.filters.currency(
        this.filteredItems.length,
        "",
        0
      )},    Total Loan Amount: ${this.$options.filters.currency(
        this.filteredItems.map((x) => x.baseLoan).reduce((a, b) => a + b)
      )}`;

      return this.excelPipeline;
    },
    addStatusFilter(statusName) {
      this.filterOptions.loanStatusName = [statusName];
      this.loanSummaryDialog = false;
      this.filterPipeline();
    },
    pageRefresh() {
      location.reload();
    },
  },
  mixins: [convert],
};
</script>
